import { CommonHash, Profile, Program, School, User } from 'app/types';
import { Document, Packer, Paragraph, TextRun, AlignmentType } from 'docx';
import jsPDF from 'jspdf';
import { saveAs } from 'file-saver';
export function navigateTop(
  yPos = 0,
  xPos = 0,
  elementId?: string,
  fromBottonElement = true,
  overElement = false
) {
  if (elementId) {
    const element = document.getElementById(elementId);
    if (element) {
      if (!overElement) {
        element.scrollTop = yPos;
        element.scrollIntoView(fromBottonElement);
      } else {
        const elementTop = element.offsetTop;
        scrollTo(0, elementTop - 100);
      }
    } else window.scrollTo(xPos, yPos);
  } else {
    window.scrollTo(xPos, yPos);
  }
}

export const dynamicScripts = (id: string, src: string, defer?: boolean, callback?: () => void) => {
  const existingScript = document.getElementById(id);
  if (!existingScript) {
    const script = document.createElement('script');
    script.id = id;
    script.src = src;
    if (defer !== undefined) script.defer = defer;
    document.body.appendChild(script);
    script.onload = () => callback && callback();
  } else if (callback) {
    callback();
  }
};

export const smoothScroll = (id: string, delay: number) => {
  setTimeout(() => {
    const element = document.getElementById(id);
    element && element.scrollIntoView({ behavior: 'smooth' });
  }, delay);
};

export const profileAnswers = (profile: Profile) => {
  const profileAnswersObj: CommonHash = {};
  Object.keys(profile).map((key: any) => {
    if (key.includes('budget')) {
      profile['budget'] !== null
        ? (profileAnswersObj['budget'] = [
            profile['budget'],
            [profile['budget_min'], profile['budget_max']],
          ])
        : null;
    } else {
      const value = profile[key as keyof Profile];
      value !== null ? (profileAnswersObj[key as keyof Profile] = value) : null;
    }
  });
  return profileAnswersObj;
};

export const shuffleArray = (array: string[]) => {
  return array.sort(() => 0.5 - Math.random());
};

export const programLabelCard = (score: number | undefined) => {
  if (!score) return null;

  score = score * 100;
  if (90 < score && score <= 100) {
    return { title: 'Perfect Match', background: '#297DF2', text: '#FFFFFF' };
  } else if (66 < score && score <= 90) {
    return { title: 'Good Match', background: '#FCEEDB', text: '#F19F3A' };
  } else {
    return { title: 'Fair Match', background: '#EBEBEB', text: '#727D9B' };
  }
};

export const schoolProgramLabelCard = (score: number | undefined) => {
  if (!score) return null;

  if (90 < score && score <= 100) {
    return { title: 'Perfect Match', background: '#ECFFEE', text: '#1BAE57' };
  } else if (66 < score && score <= 90) {
    return { title: 'Good Match', background: '#FCEEDB', text: '#F19F3A' };
  } else {
    return { title: 'Fair Match', background: '#EBEBEB', text: '#727D9B' };
  }
};

export const shortlistLabelCard = (preferencesScore: number | undefined) => {
  if (!preferencesScore) return null;

  preferencesScore = preferencesScore * 100;

  if (66 < preferencesScore && preferencesScore <= 100) {
    return { title: 'best match', background: '#FCEEDB', text: '#F29625', width: '105px' };
  } else if (preferencesScore <= 66) {
    return { title: 'competitive profile', background: '#E0FDE0', text: '#39C339', width: '138px' };
  } else {
    return null;
  }
};

export const getStepsToShow = (
  lastStepDone: number,
  profileCompleted: boolean | undefined,
  preferencesFilled: boolean | undefined,
  meetingScheduled: boolean | undefined,
  listOfProgramsAsked: boolean | undefined,
  schoolShortlisted: boolean | undefined,
  applicationStarted: boolean | undefined,
  cvUploaded: boolean | undefined,
  testsScoresUploaded: boolean | undefined,
  applicationSubmitted: boolean | undefined
) => {
  const stepsLength = lastStepDone ? 2 : 3;
  const indexOfSteps: Array<number> = [];
  if (!profileCompleted && indexOfSteps.length < stepsLength) {
    indexOfSteps.push(0);
  }
  if (!preferencesFilled && indexOfSteps.length < stepsLength) {
    indexOfSteps.push(1);
  }
  if (!meetingScheduled && indexOfSteps.length < stepsLength) {
    indexOfSteps.push(2);
  }
  if (!listOfProgramsAsked && indexOfSteps.length < stepsLength) {
    indexOfSteps.push(3);
  }
  if (!schoolShortlisted && indexOfSteps.length < stepsLength) {
    indexOfSteps.push(4);
  }
  if (!applicationStarted && indexOfSteps.length < stepsLength && schoolShortlisted) {
    indexOfSteps.push(5);
  }
  if (!cvUploaded && indexOfSteps.length < stepsLength && applicationStarted) {
    indexOfSteps.push(6);
  }
  if (!testsScoresUploaded && indexOfSteps.length < stepsLength && cvUploaded) {
    indexOfSteps.push(7);
  }
  if (!applicationSubmitted && indexOfSteps.length < stepsLength && testsScoresUploaded) {
    indexOfSteps.push(8);
  }
  return indexOfSteps;
};

export function getPercentageNextSteps(args: Array<boolean | undefined>) {
  let percentage = 0;
  for (let i = 0; i < args.length; i++) {
    args[i] ? (percentage += 10) : undefined;
  }
  return percentage;
}

export const programLabelDetail = (score: number | undefined) => {
  if (!score && score !== 0) return null;
  if (90 < score && score <= 100) {
    return 'High';
  } else if (66 < score && score <= 90) {
    return 'High';
  } else if (32 < score && score <= 66) {
    return 'Fair';
  } else {
    return 'Low';
  }
};

export const userInitials = (user: User | null) => {
  if (user && user.name) {
    const listName = user.name.split(' ');
    const filtered = listName.filter((str) => str != '');
    return filtered[0]
      ? filtered.length > 1
        ? `${filtered[0][0]?.toUpperCase()}${filtered[1][0]?.toUpperCase()}`
        : filtered[0][0].toUpperCase()
      : '';
  } else {
    return '';
  }
};

export const userAnswers = (user: User | null, answers: CommonHash) => {
  if (!user) return {};
  const userAnswersObj: CommonHash = {};
  Object.keys(user).map((key: any) => {
    const value = user[key as keyof User];
    value !== null ? (userAnswersObj[key as keyof User] = value) : null;
  });
  Object.keys(answers).map((key: any) => {
    const value = answers[key as keyof User];
    value !== null ? (userAnswersObj[key as keyof User] = value) : null;
  });
  return userAnswersObj;
};

export const updateProfileAnswers = (user: User, profile: Profile | undefined) => {
  const updtatedAnswers: any = {} as Profile;
  const testQuestions = ['english_exam', 'score_english_exam', 'score_technical_exam'];
  if (profile) {
    Object.keys(profile).map((key: string) => {
      updtatedAnswers[`${key}`] = profile[key as keyof Profile];
    });
  }
  if (user.academic_history && user.academic_history.length) {
    if (user.academic_history[0].school_name) {
      updtatedAnswers['school_name'] = user['academic_history'][0].school_name;
    }
    if (user.academic_history[0].degree_type) {
      updtatedAnswers.degree_type = user['academic_history'][0].degree_type;
    }
    if (user.academic_history[0].subject_type) {
      updtatedAnswers.subject_type = user['academic_history'][0].subject_type;
    }
    if (user.academic_history[0].grade) {
      updtatedAnswers.grade = user['academic_history'][0].grade;
    }
  }
  testQuestions.forEach((q: string) => {
    if (user[q as keyof User]) {
      updtatedAnswers[`${q}`] = user[q as keyof User];
      if (q == 'english_exam') updtatedAnswers['english_level'] = 'exam';
    }
  });
  return updtatedAnswers;
};

export const updateUserAnswers = (user: User, profile: Profile | undefined) => {
  const updtatedAnswers: any = {} as User;
  const testQuestions = ['english_exam', 'score_english_exam', 'score_technical_exam'];
  const englishExamQuestions = [
    'english_exam',
    'score_english_exam',
    'english_exam_reading',
    'english_exam_listening',
    'english_exam_speaking',
    'english_exam_writing',
    'english_exam_date',
  ];
  Object.keys(user).map((key: string) => {
    if (key == 'academic_history') {
      const histories: CommonHash[] = [];
      if (user.academic_history && user.academic_history?.[0]?.length) {
        user.academic_history?.forEach((history) => {
          const h: CommonHash = {};
          Object.keys(history).map((k) => {
            h[k] = history[k];
          });
          histories.push(h);
        });
      } else {
        histories.push({});
      }
      histories[0].school_name = profile?.school_name;
      histories[0].degree_type = profile?.degree_type;
      histories[0].subject_type = profile?.subject_type;
      histories[0].grade = profile?.grade;
      updtatedAnswers['academic_history'] = histories;
    } else {
      updtatedAnswers[`${key}`] = user[key as keyof User];
    }
  });
  testQuestions.forEach((q) => {
    if (profile?.[q as keyof Profile]) updtatedAnswers[`${q}`] = profile?.[q as keyof Profile];
  });
  if (profile?.['english_level'] != 'exam' && user.english_exam) {
    englishExamQuestions.forEach((q) => {
      updtatedAnswers[`${q}`] = null;
    });
  }
  return updtatedAnswers;
};

export const countMatchedElements = (
  array1: string[],
  answers: CommonHash,
  special_key: any = false
) => {
  let count = 0;
  array1.forEach((elem) => {
    if (special_key) {
      if (
        Object.keys(answers[`${special_key}`]).length > 0 &&
        Object.keys(answers[`${special_key}`][0]).includes(elem) &&
        answers[special_key][0][elem]
      )
        count++;
    } else {
      if (Object.keys(answers).includes(elem) && answers[elem] != null) count++;
    }
  });
  return count;
};

export const getCookie = (cookie: string) => {
  const cookies = document.cookie;
  return cookies.includes(cookie) ? cookies.split(cookie)[1].split('=')[1].split(';')[0] : '';
};

export const capitalizeFirstLetter = (word?: string) => {
  return word ? word.charAt(0).toUpperCase() + word.slice(1) : '';
};

export function entityIsSchool(
  toBeDetermined: School | Program | undefined
): toBeDetermined is School {
  return toBeDetermined !== undefined && (toBeDetermined as School).city !== undefined;
}

export function entityIsProgram(
  toBeDetermined: School | Program | undefined
): toBeDetermined is Program {
  return toBeDetermined !== undefined && (toBeDetermined as Program).school !== undefined;
}
export const checkOverflow = (divReferenced: any): boolean => {
  if (divReferenced) return divReferenced.offsetWidth < divReferenced.scrollWidth;
  return false;
};

export const generateWordDocument = (
  responseGpt: string | undefined,
  psName: string,
  sendGTMEvent: (eventName: string) => void,
  setPsWordDocument?: (value: React.SetStateAction<File | undefined>) => void,
  save?: boolean
) => {
  const paragraphes = responseGpt?.split('\n');
  if (paragraphes && psName) {
    const doc = new Document({
      sections: [
        {
          properties: {},
          children: paragraphes.map(
            (paragraph) =>
              new Paragraph({
                children: [
                  new TextRun({
                    text: paragraph,
                    font: 'Arial',
                    size: 20,
                  }),
                ],
                alignment: AlignmentType.BOTH,
              })
          ),
        },
      ],
    });
    Packer.toBlob(doc).then((blob) => {
      if (save) {
        saveAs(blob, `${psName}.docx`);
        sendGTMEvent('ps_builder_download_docx');
      }
      if (setPsWordDocument) {
        setPsWordDocument(new File([blob], `${psName}.docx`));
      }
    });
  }
};

export const generatePdf = (
  responseGpt: string | undefined,
  psName: string,
  sendGTMEvent: (eventName: string) => void,
  setShowDownloadModal: (value: React.SetStateAction<boolean>) => void
) => {
  const docPdf = new jsPDF();
  if (responseGpt && psName) {
    docPdf.setFontSize(10);
    const splittedByBreakline = responseGpt.split('\n');

    let distanceTop = 20;

    splittedByBreakline.map((paragraph: string) => {
      docPdf.text(paragraph, 20, distanceTop, {
        maxWidth: 170,
        align: 'justify',
      });

      const splittedByLine = docPdf.splitTextToSize(paragraph, 170);

      if (docPdf.getTextWidth(paragraph) > 0) {
        distanceTop = distanceTop + splittedByLine.length * 5;
      }
    });
    docPdf.save(psName);
    sendGTMEvent('ps_builder_download_pdf');
    setShowDownloadModal(false);
  }
};
